// reason: Cytoscape LayoutOptions is a discriminated union over layout names
// (cose-bilkent, dagre, breadthfirst, circle, …) where each carries its own
// option keys; building the union manually here would duplicate Cytoscape's
// types without runtime benefit.
/* eslint-disable @typescript-eslint/no-explicit-any */

export type LayoutType = 'attack-path' | 'tree' | 'force' | 'layered';
export type Orientation = 'vertical' | 'horizontal';

export interface LayoutConfigOptions {
  compact?: boolean;
  orientation?: Orientation;
}

export function getLayoutConfig(layout: LayoutType, options?: LayoutConfigOptions): any {
  const compact = options?.compact ?? false;
  const orientation = options?.orientation ?? 'vertical';
  const pad = compact ? 30 : 50;

  if (layout === 'attack-path') {
    return {
      name: 'dagre',
      rankDir: orientation === 'vertical' ? 'TB' : 'LR',
      // nodeSep must be >= the SVG renderer's node label container (140px,
      // ObsidianGraph.tsx). Node separation and label width both scale by the same
      // zoom factor, so a smaller nodeSep makes same-rank labels overlap at ANY zoom.
      // The SVG renderer's headless solve always uses the non-compact value (170);
      // `compact` only feeds the legacy Cytoscape configs (kept above the label width too).
      nodeSep: compact ? 120 : 170,
      // a touch more vertical room so a node's 2-line label doesn't crowd the rank below
      rankSep: compact ? 90 : (orientation === 'vertical' ? 120 : 140),
      edgeSep: 20,
      animate: true,
      animationDuration: 400,
      padding: pad,
      fit: true,
    };
  }

  if (layout === 'tree') {
    return {
      name: 'breadthfirst',
      directed: true,
      spacingFactor: compact ? 1.6 : 2.2,
      animate: true,
      animationDuration: 400,
      padding: pad,
    };
  }

  if (layout === 'layered') {
    // The layered layout is implemented as a custom solver in
    // `headlessLayout.ts::runLayout` (rank = 6-layer ontology, X = greedy
    // barycentric within rank). Returning a `preset` config here keeps the
    // type union honest but the solver path bypasses Cytoscape's layout
    // engine entirely for this case.
    return { name: 'preset', animate: false, fit: true, padding: pad };
  }

  // force (cose)
  return {
    name: 'cose',
    animate: true,
    animationDuration: 400,
    nodeRepulsion: () => 30000,
    idealEdgeLength: () => compact ? 120 : 180,
    gravity: 0.05,
    padding: pad,
    nodeOverlap: 20,
    numIter: 1000,
    initialTemp: 300,
  };
}

/** Returns edge style config that adapts curve-style to the layout and orientation. */
export function getEdgeStyleForLayout(layout: LayoutType, orientation: Orientation): Record<string, unknown> {
  if (layout === 'attack-path') {
    return {
      'curve-style': 'taxi',
      'taxi-direction': orientation === 'vertical' ? 'downward' : 'rightward',
      'taxi-turn': 30,
      'target-distance-from-node': 2,
    };
  }
  return {
    'curve-style': 'unbundled-bezier',
    'control-point-distances': [45],
    'control-point-weights': [0.5],
  };
}
